import { useRef, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
// Fix per icone Leaflet in Vite
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { translatePOIs, useApp, useTenantApp } from '../../hooks';

// Hooks personalizzati
import {
    useMapInitialization,
    useUserMarker,
    useParticipantMarkers,
    usePOIMarkers,
    usePoisWithLiveUpdates,
    useLiveModalPoi
} from './hooks';

// Componenti
import { MapControls } from './MapControls';
import POIModal from './POIModal';
import type { TranslatedPOI } from '@/types/poi';

// Fix icona predefinita
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

interface MapProps {
    controlPanelPOI: string | null;
}

export default function Mappa({ controlPanelPOI }: MapProps) {
    const { t, i18n } = useTranslation();

    const { position: userPosition, roomSession, participants } = useApp();
    const { app, mapBlobUrl } = useTenantApp();
    const { pois, mapConfig } = app!;
    const { bounds: mapBounds, zoom, minZoom, maxZoom, center, tileUrl, imageUrl } = mapConfig;

    const mapContainerRef = useRef<HTMLDivElement>(null);

    const [selectedPOIId, setSelectedPOIId] = useState<string | null>(null);
    const [isPoiModalOpen, setIsPoiModalOpen] = useState(false);

    const handlePOIClick = (poi: TranslatedPOI) => {
        setSelectedPOIId(poi.id);
        setIsPoiModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsPoiModalOpen(false);
        // Keep POI data for smooth transition
        setTimeout(() => setSelectedPOIId(null), 300);
    };

    // get POIS with live updates
    const effectivePois = usePoisWithLiveUpdates(pois, app?.segmentId || null);

    // Selected POI (base data) to attach the live listener for the modal
    const selectedBasePOI = useMemo(() => {
        if (!selectedPOIId) return null;
        return effectivePois.find(p => p.id === selectedPOIId) || null;
    }, [selectedPOIId, effectivePois]);

    // Single POI realtime updates when the modal is open
    const liveModalPoi = useLiveModalPoi(
        selectedPOIId,
        app?.segmentId || null,
        isPoiModalOpen
    );

    const effectiveLiveModalPoi =
        isPoiModalOpen && liveModalPoi && liveModalPoi.id === selectedPOIId
            ? liveModalPoi
            : null;

    // Translate POIs based on the current language
    const translatedPOIs = useMemo(() => {
        return translatePOIs(effectivePois, i18n.language);
    }, [effectivePois, i18n.language]);

    // Derive the selected POI: prioritize live modal data, fallback to base POI
    const selectedPOI = useMemo(() => {
        const sourcePoi = effectiveLiveModalPoi ?? selectedBasePOI;
        if (!sourcePoi) return null;

        const [translated] = translatePOIs([sourcePoi], i18n.language);
        return translated || null;
    }, [effectiveLiveModalPoi, selectedBasePOI, i18n.language]);

    // Initialize map and overlay
    const { mapRef, isMapReady } = useMapInitialization({
        mapContainerRef,
        tileUrl,
        imageUrl,
        mapBlobUrl,
        mapBounds,
        center,
        zoom,
        minZoom,
        maxZoom
    });

    // Not Logged-in user marker management
    useUserMarker({
        mapRef,
        isMapReady,
        userPosition,
        roomSession,
        t,
        language: i18n.language
    });

    // Participant marker management
    useParticipantMarkers({
        mapRef,
        participants,
        roomSession,
        t,
        language: i18n.language,
        // hasUserPosition is true if the user has a valid position, false otherwise
        // This is used to determine if we should show participant markers or not
        // not rerendering participant markers if the user changes position
        hasUserPosition: Boolean(userPosition)
    });

    // POI marker management
    usePOIMarkers({
        mapRef,
        translatedPOIs,
        onPOIClick: handlePOIClick
    });

    useEffect(() => {
        if (!controlPanelPOI) return;

        // Defers updates to avoid synchronous setState inside the effect.
        const timeoutId = setTimeout(() => {
            setSelectedPOIId(controlPanelPOI);
            setIsPoiModalOpen(true);
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [controlPanelPOI]);

    return (
        <div className="w-full h-full relative">
            <div
                ref={mapContainerRef}
                className="map-fullscreen hide-scrollbar"
            />
            <MapControls
                mapRef={mapRef}
                userPosition={userPosition}
            />
            <POIModal
                poi={selectedPOI}
                isOpen={isPoiModalOpen}
                position={userPosition}
                onClose={handleCloseModal}
            />
        </div>
    );
}
